import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { logEvent } from "firebase/analytics";

import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";

import type { Interview } from "@aglocal/schema/Interview";
import {
  POST_MEETING_LIMIT_FIELDS,
  type PostMeetingFieldErrors,
} from "@aglocal/schema/PostMeetingLimits";

import { useMissionControl } from "@aglocal/web/mission-control";

import Container from "@aglocal/web/components/Container";
import { Heading } from "@aglocal/web/components/Typography";

import {
  useDocSnapshot,
  useFirebaseAnalytics,
  useMissionControlDB,
} from "@aglocal/web/firebase";
import useDarkquell from "@aglocal/web/firebase/queries/useDarkquell";

import asError from "@aglocal/web/helpers/asError";
import useMatchMedia from "@aglocal/web/hooks/useMatchMedia";
import useReportError from "@aglocal/web/hooks/useReportError";

import Stack from "@aglocal/web/components/Stack";

import useSetDefault from "@/hooks/useSetDefault";

import ErrorPage from "@/pages/Error";
import LoadingPage from "@/pages/Loading";

import {
  getConnectedDetailsUpdate,
  PostMeetingFormWithSummary,
  usePostMeetingFormWithSummary,
} from "./form";
import PostMeetingConfirmation from "./PostMeetingConfirmation";
import PostMeetingNotEditable from "./PostMeetingNotEditable";
import SaveResultsHandler from "./SaveResultsHandler";
import PostMeetingDrawer from "./PostMeetingDrawer";
import PostMeetingFooter from "./PostMeetingFooter";
import LoadingDialog from "./LoadingDialog";

import CompanyField from "./fields/CompanyField";
import DetailsField from "./fields/DetailsField";
import GoalsAndInterestsField from "./fields/GoalsAndInterestsField";
import LocationCommentsField from "./fields/LocationCommentsField";
import NotesField from "./fields/NotesField";
import SkillsCommentsField from "./fields/SkillsCommentsField";
import SkillsField from "./fields/SkillsField";
import SummaryField from "./fields/SummaryField";
import TitleField from "./fields/TitleField";
import useSkillClassifier from "@aglocal/web/firebase/queries/useSkillClassifier";

const useStyles = makeStyles({
  root: {
    height: "100vh",
    display: "grid",
    overflow: "hidden",
    gridTemplateAreas: `
        "header"
        "main"
        "footer"
      `,
    gridTemplateRows: "auto minmax(0, 1fr) auto",
  },
  header: {
    overflow: "auto",
    gridArea: "header",
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  content: {
    gridArea: "main",
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
  },
  main: {
    overflowX: "hidden",
    overflowY: "auto",
    position: "relative",
    flexGrow: 1,
    flexBasis: 0,
  },
  container: {
    maxWidth: "720px",
    rowGap: tokens.spacingVerticalM,
    columnGap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
  },
  section: {
    flexGrow: 1,
    flexBasis: "24rem",
  },
  footer: {
    gridArea: "footer",
  },
});

interface ConfirmStateClosed {
  show: false;
}

interface ConfirmStateOpen {
  show: true;
  data: PostMeetingFormWithSummary;
}

type ConfirmState = ConfirmStateClosed | ConfirmStateOpen;

export interface PostMeetingTabProps {
  interview: Interview;
}

export default function PostMeetingTab({ interview }: PostMeetingTabProps) {
  const { $t } = useIntl();
  const styles = useStyles();

  const api = useMissionControl();
  const db = useMissionControlDB();
  const analytics = useFirebaseAnalytics();

  const isXL = useMatchMedia({ min: "xl" });
  const [drawerIsOpen, setDrawerIsOpen] = useState<boolean | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>({ show: false });
  const [showSaveHandler, setShowSaveHandler] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isDismissedNotEditableDialog, setIsDismissedNotEditableDialog] =
    useState(false);

  const {
    snapshot: detailsSnapshot,
    loading: detailsSnapshotLoading,
    error: detailsError,
  } = useDocSnapshot(db.talentMeetingDetails.doc(interview.id));
  const details =
    detailsSnapshot?.id === interview.id ? detailsSnapshot.data() : undefined;
  const detailsLoading =
    detailsSnapshotLoading ||
    (detailsSnapshot != null && detailsSnapshot.id !== interview.id);

  const {
    control,
    getFieldState,
    getValues,
    handleSubmit,
    reset,
    resetField,
    setError,
    setValue,
    isMeetingTypeDisabled,
    isLoadingMetrics,
    metricsError,
    profileNotes,
    disableSave,
  } = usePostMeetingFormWithSummary(interview, details);

  const setDefault = useSetDefault({
    getFieldState,
    getValues,
    resetField,
    setValue,
  });

  const [skills, darkquellLoading, darkquellError] = useDarkquell(interview.id);
  const [negativeSkills, skillClassifierLoading, skillClassifierError] =
    useSkillClassifier(interview.id);

  const removedSkills = useMemo(
    () => negativeSkills.map((skills) => skills.skill),
    [negativeSkills],
  );

  useReportError(darkquellError);
  useReportError(detailsError);
  useReportError(skillClassifierError);

  const loading =
    darkquellLoading ||
    detailsLoading ||
    skillClassifierLoading ||
    isLoadingMetrics;
  const error =
    metricsError ?? darkquellError ?? detailsError ?? skillClassifierError;

  if (interview.interviewee?.connectedDetails == null) {
    // We should never reach hear instead of the Contact Selector Page
    return (
      <ErrorPage
        title={$t({
          defaultMessage: "Connected Details Not Found",
          description:
            "Error shown when the wrap-up page opens for a talent that has no Connected profile linked.",
        })}
      />
    );
  }

  const talentConnectedDetails = interview.interviewee.connectedDetails;

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <ErrorPage
        title={$t({
          defaultMessage: "Error loading meeting",
          description:
            "Error shown when the details of the meeting could not be loaded.",
        })}
      />
    );
  }

  // Validation runs in the form resolver; a valid form asks for confirmation
  const onSubmit = handleSubmit((data) => {
    setConfirm({ show: true, data });
  });

  const handleConfirm = async (data: PostMeetingFormWithSummary) => {
    setConfirm({ show: false });

    try {
      logEvent(analytics, "meeting_summary_submitted", {
        iid: interview.id,
        name: data.summary.name,
        settings: data.summary.settings,
        edited: getFieldState("summary.value").isDirty,
      });
    } catch (error) {
      reportError(error);
    }

    setIsSubmitting(true);
    let hasFieldErrors = false;
    try {
      const update = getConnectedDetailsUpdate(
        data,
        talentConnectedDetails.skills ?? [],
      );
      const result = await api.updateConnectedDetails(update);
      const hasError = result.status !== "ok";
      if (hasError) {
        const serverErrors = result.fieldErrors as
          | PostMeetingFieldErrors
          | undefined;
        hasFieldErrors = POST_MEETING_LIMIT_FIELDS.some(
          (field) => typeof serverErrors?.[field] === "string",
        );
        if (hasFieldErrors) {
          // isSubmitSuccessful disables every field; reset it first so the recruiter can fix the flagged values
          handleCancel();
          for (const field of POST_MEETING_LIMIT_FIELDS) {
            const message = serverErrors?.[field];
            if (typeof message === "string") {
              setError(field, { type: "server", message });
            }
          }
        }
      }
      setSubmitSuccess(!hasError);
    } catch (error: unknown) {
      setSubmitSuccess(false);
      reportError(asError(error));
    } finally {
      setIsSubmitting(false);
      // Limit violations render next to their fields, so skip the generic failure dialog
      setShowSaveHandler(!hasFieldErrors);
    }
  };

  const handleCancel = () => {
    setConfirm({ show: false });
    reset(undefined, {
      keepDirtyValues: true,
      keepErrors: true,
      keepDirty: true,
      keepValues: true,
      keepDefaultValues: true,
      keepTouched: true,
      keepIsValidating: true,
      keepIsValid: true,
      keepSubmitCount: true,
    });
  };

  return (
    <form
      className={mergeClasses(styles.root)}
      onSubmit={(event) => {
        void onSubmit(event);
      }}
    >
      <LoadingDialog
        isOpen={isSubmitting}
        message={$t({
          defaultMessage: "Updating Connected...",
          description:
            "Shown with a spinner while the wrap-up form is being saved to Connected.",
        })}
      />

      {disableSave && !isDismissedNotEditableDialog && (
        <PostMeetingNotEditable
          onConfirm={() => {
            // Open Salesforce URL in new tab
            const salesforceUrl = `https://allegisgroup.lightning.force.com/lightning/r/Contact/${interview.interviewee?.connectedDetails?.contactId}/view`;
            window.open(salesforceUrl, "_blank");
            setIsDismissedNotEditableDialog(true);
          }}
          onCancel={() => {
            // Close the dialog
            setIsDismissedNotEditableDialog(true);
          }}
        />
      )}

      {showSaveHandler && (
        <SaveResultsHandler
          isError={!submitSuccess}
          onCancel={() => {
            setShowSaveHandler(false);
            if (!submitSuccess) {
              handleCancel();
            }
          }}
        />
      )}

      {confirm.show && (
        <PostMeetingConfirmation
          onConfirm={() => {
            void handleConfirm(confirm.data);
          }}
          onCancel={() => {
            handleCancel();
          }}
        />
      )}

      <header className={styles.header}>
        <Container>
          <Heading variant="subtitle1">
            {talentConnectedDetails.displayName ? (
              <FormattedMessage
                defaultMessage="Post meeting conversation with {name}"
                description="Title of the wrap-up page; {name} is the talent's name."
                values={{ name: talentConnectedDetails.displayName }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Post meeting conversation"
                description="Title of the wrap-up page when the talent's name is unknown."
              />
            )}
          </Heading>
        </Container>
      </header>

      <div className={styles.content}>
        <main className={styles.main}>
          <Container className={styles.container}>
            <Stack className={styles.section} gap="m">
              <TitleField
                interview={interview}
                details={details}
                control={control}
                setDefault={setDefault}
              />
              <CompanyField
                currentValue={profileNotes?.currentCompany}
                control={control}
              />
              {/* Date Available */}
              <NotesField control={control} />
              <SummaryField
                interview={interview}
                control={control}
                getFieldState={getFieldState}
                getValues={getValues}
                resetField={resetField}
                setValue={setValue}
              />
              <SkillsField
                existingSkills={talentConnectedDetails.skills}
                suggestedSkills={details?.skills}
                negativeSkills={removedSkills}
                control={control}
                setDefault={setDefault}
              />
              <SkillsCommentsField control={control} />
              <GoalsAndInterestsField control={control} />
              <LocationCommentsField control={control} />
              <DetailsField
                details={details}
                control={control}
                setDefault={setDefault}
              />
            </Stack>
          </Container>
        </main>
        <PostMeetingDrawer
          isOpen={drawerIsOpen ?? isXL}
          onOpenChange={setDrawerIsOpen}
          interview={interview}
          skills={skills}
          negativeSkills={negativeSkills}
        />
      </div>

      <PostMeetingFooter
        className={styles.footer}
        control={control}
        drawerIsOpen={drawerIsOpen ?? isXL}
        setDrawerIsOpen={setDrawerIsOpen}
        interview={interview}
        submitSuccess={submitSuccess}
        isMeetingTypeDisabled={isMeetingTypeDisabled}
        disableSave={disableSave}
      />
    </form>
  );
}
